import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const CreatePortfolio = () => {
  const navigate = useNavigate();
  const [portfolioName, setPortfolioName] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [assets, setAssets] = useState([{ symbol: '', percentage: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Вычисляем общий процент и валидность
  const totalPercentage = assets.reduce((sum, asset) => {
    const percentage = parseFloat(asset.percentage) || 0;
    return sum + percentage;
  }, 0);

  const isFormValid = () => {
    // Проверяем основные поля
    if (!portfolioName.trim() || !totalAmount || parseFloat(totalAmount) <= 0) {
      return false;
    }

    // Проверяем что есть хотя бы один актив с заполненными полями
    const validAssets = assets.filter(asset =>
      asset.symbol.trim() !== '' &&
      asset.percentage !== '' &&
      parseFloat(asset.percentage) > 0
    );

    if (validAssets.length === 0) {
      return false;
    }

    // Проверяем что все активы в списке заполнены корректно
    const allAssetsFilled = assets.every(asset => {
      if (asset.symbol.trim() === '' && asset.percentage === '') {
        return true; // Пустые строки разрешены
      }
      return asset.symbol.trim() !== '' &&
             asset.percentage !== '' &&
             parseFloat(asset.percentage) > 0;
    });

    if (!allAssetsFilled) {
      return false;
    }

    // Проверяем что общий процент равен 100
    return Math.abs(totalPercentage - 100) < 0.01; // Допускаем погрешность 0.01%
  };

  // Добавить новый актив
  const addAsset = () => {
    if (totalPercentage < 100) {
      setAssets([...assets, { symbol: '', percentage: '' }]);
    }
  };

  // Удалить актив
  const removeAsset = (index) => {
    if (assets.length > 1) {
      const newAssets = assets.filter((_, i) => i !== index);
      setAssets(newAssets);
    }
  };

  // Обновить актив
  const updateAsset = (index, field, value) => {
    const newAssets = [...assets];
    newAssets[index][field] = value;
    setAssets(newAssets);
  };

  // Предустановленные популярные активы
  const popularAssets = [
    'CASH', 'VOO', 'QQQ', 'SCHD', 'JEPI', 'TLT', 'VGSH', 'TBIL', 'VTI', 'VEA', 'VWO', 'BND'
  ];

  // Сохранить портфель
  const savePortfolio = async () => {
    if (!isFormValid()) return;

    setLoading(true);
    setError('');

    try {
      const portfolioData = {
        name: portfolioName,
        total_amount: parseFloat(totalAmount),
        assets: assets.map(asset => ({
          symbol: asset.symbol.toUpperCase(),
          percentage: parseFloat(asset.percentage),
          amount: (parseFloat(totalAmount) * parseFloat(asset.percentage)) / 100
        })),
        created_at: new Date().toISOString()
      };

      const response = await api.post('/portfolios', portfolioData);

      if (response.status === 201) {
        // Успешно создано - переходим на дашборд
        navigate('/', {
          state: {
            message: `Портфель "${portfolioName}" успешно создан!`,
            portfolioId: response.data.id
          }
        });
      }
    } catch (error) {
      console.error('Ошибка создания портфеля:', error);
      setError('Ошибка при сохранении портфеля. Попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-portfolio">
      <div className="header">
        <button
          className="back-button"
          onClick={() => navigate('/')}
          aria-label="Назад на главную"
        >
          ← Назад
        </button>
        <h1>Создать новый портфель</h1>
      </div>

      <div className="portfolio-form">
        {/* Основная информация */}
        <div className="form-section">
          <h2>Основная информация</h2>

          <div className="form-group">
            <label htmlFor="portfolio-name">Название портфеля:</label>
            <input
              id="portfolio-name"
              type="text"
              value={portfolioName}
              onChange={(e) => setPortfolioName(e.target.value)}
              placeholder="Например: Консервативный портфель"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="total-amount">Общая сумма инвестиций ($):</label>
            <input
              id="total-amount"
              type="number"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              placeholder="100000"
              min="1"
              step="0.01"
            />
          </div>
        </div>

        {/* Активы */}
        <div className="form-section">
          <div className="assets-header">
            <h2>Состав портфеля</h2>
            <div className="percentage-indicator">
              <span className={`total-percentage ${Math.abs(totalPercentage - 100) < 0.01 ? 'valid' : totalPercentage > 100 ? 'invalid' : ''}`}>
                {totalPercentage.toFixed(1)}% из 100%
              </span>
              {/* Отладочная информация */}
              <div style={{fontSize: '0.8rem', color: '#666', marginTop: '0.5rem'}}>
                Форма валидна: {isFormValid() ? '✅' : '❌'} |
                Название: {portfolioName ? '✅' : '❌'} |
                Сумма: {totalAmount && parseFloat(totalAmount) > 0 ? '✅' : '❌'} |
                Проценты: {Math.abs(totalPercentage - 100) < 0.01 ? '✅' : `❌ ${totalPercentage.toFixed(1)}%`}
              </div>
            </div>
          </div>

          <div className="assets-list">
            {assets.map((asset, index) => (
              <div key={index} className="asset-item">
                <div className="asset-controls">
                  <div className="asset-input-group">
                    <input
                      type="text"
                      value={asset.symbol}
                      onChange={(e) => updateAsset(index, 'symbol', e.target.value.toUpperCase())}
                      placeholder="Тикер (например: VOO)"
                      list={`popular-assets-${index}`}
                      className="symbol-input"
                    />
                    <datalist id={`popular-assets-${index}`}>
                      {popularAssets.map(symbol => (
                        <option key={symbol} value={symbol} />
                      ))}
                    </datalist>

                    <input
                      type="number"
                      value={asset.percentage}
                      onChange={(e) => updateAsset(index, 'percentage', e.target.value)}
                      placeholder="% доля"
                      min="0"
                      max="100"
                      step="0.1"
                      className="percentage-input"
                    />
                    <span className="percentage-symbol">%</span>
                  </div>

                  <div className="asset-actions">
                    {assets.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAsset(index)}
                        className="remove-asset-btn"
                        aria-label="Удалить актив"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Показываем сумму в долларах */}
                {asset.percentage && totalAmount && (
                  <div className="asset-amount">
                    ≈ ${((parseFloat(totalAmount) * parseFloat(asset.percentage)) / 100).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Кнопка добавления актива */}
          <button
            type="button"
            onClick={addAsset}
            className="add-asset-btn"
            disabled={totalPercentage >= 100}
          >
            + Добавить актив
          </button>

          {/* Подсказки */}
          <div className="hints">
            <p>💡 <strong>Популярные ETF:</strong></p>
            <ul>
              <li><strong>VOO/VTI</strong> - Широкий рынок США</li>
              <li><strong>QQQ</strong> - Технологические компании</li>
              <li><strong>SCHD/JEPI</strong> - Дивидендные акции</li>
              <li><strong>TLT/BND</strong> - Облигации США</li>
              <li><strong>CASH</strong> - Наличные средства</li>
            </ul>
          </div>
        </div>

        {/* Сводка */}
        {portfolioName && totalAmount && assets.length > 0 && (
          <div className="portfolio-summary">
            <h3>Предварительная сводка</h3>
            <div className="summary-item">
              <strong>Портфель:</strong> {portfolioName}
            </div>
            <div className="summary-item">
              <strong>Общая сумма:</strong> ${parseFloat(totalAmount || 0).toLocaleString()}
            </div>
            <div className="summary-assets">
              <strong>Активы:</strong>
              <ul>
                {assets.filter(asset => asset.symbol && asset.percentage).map((asset, index) => (
                  <li key={index}>
                    {asset.symbol}: {asset.percentage}%
                    (${((parseFloat(totalAmount) * parseFloat(asset.percentage)) / 100).toLocaleString()})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Ошибки */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Кнопки управления */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="cancel-btn"
          >
            Отмена
          </button>

          <button
            type="button"
            onClick={savePortfolio}
            disabled={!isFormValid() || loading}
            className="save-btn"
          >
            {loading ? 'Сохранение...' : 'Сохранить портфель'}
          </button>
        </div>

        {/* Валидационные подсказки */}
        {!isFormValid() && (portfolioName || totalAmount || assets.some(a => a.symbol || a.percentage)) && (
          <div className="validation-hints">
            <h4>Для сохранения нужно:</h4>
            <ul>
              {!portfolioName.trim() && <li>✗ Указать название портфеля</li>}
              {(!totalAmount || parseFloat(totalAmount) <= 0) && <li>✗ Указать положительную сумму инвестиций</li>}
              {Math.abs(totalPercentage - 100) >= 0.01 && <li>✗ Распределить ровно 100% между активами (сейчас {totalPercentage.toFixed(1)}%)</li>}
              {assets.some(asset => {
                if (asset.symbol.trim() === '' && asset.percentage === '') return false;
                return !asset.symbol.trim() || asset.percentage === '' || parseFloat(asset.percentage) <= 0;
              }) && <li>✗ Заполнить все поля активов корректно</li>}
              {assets.filter(asset => asset.symbol.trim() !== '' && asset.percentage !== '').length === 0 &&
                <li>✗ Добавить хотя бы один актив</li>}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePortfolio;
